import { useCallback, useMemo, useState } from 'react';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { useOpenPresaveWizardForSong } from '../wizards/presaveLink/useOpenPresaveWizardForSong';
import { catalogCreationSource } from './catalogCreationSource';

/**
 * The modals a catalog row opens: remove and the creation sheet. The song files, the service
 * links and the ownership are on the song page now (design song-page a2).
 *
 * An unreleased row opens the pre-save screen of the song (design presave-create a1): its page is
 * always a pre-save, made from the row. A claimed row opens the creation sheet. The two keys differ because they start from different things: a claimed row
 * resolves a public catalog track by its maid, while an unreleased one is built out of the row
 * itself and tied back to it by uuid.
 */
export function useCatalogRowModals() {
  const [itemToRemove, setItemToRemove] = useState<CatalogItem>();
  const [createFor, setCreateFor] = useState<CatalogItem>();
  // design catalog-merge a2: the row whose menu opened "Merge with another song…".
  const [itemToMerge, setItemToMerge] = useState<CatalogItem>();
  const closeMerge = useCallback(() => setItemToMerge(undefined), []);

  const closeRemove = useCallback(() => setItemToRemove(undefined), []);
  const closeCreate = useCallback(() => setCreateFor(undefined), []);
  const openPresave = useOpenPresaveWizardForSong();
  const openCreate = useCallback(
    (item: CatalogItem) => {
      if (item.kind === CatalogItemKind.unreleased) {
        openPresave(item.uuid);
        return;
      }
      setCreateFor(item);
    },
    [openPresave]
  );

  // A row we hold a real catalog track for opens the sheet by maid; everything
  // else opens by uuid and is resolved out of the row itself.
  const creationSource = useMemo(() => catalogCreationSource(createFor), [createFor]);

  return {
    itemToRemove,
    openRemove: setItemToRemove,
    closeRemove,
    createFor,
    openCreate,
    closeCreate,
    creationSource,
    itemToMerge,
    openMerge: setItemToMerge,
    closeMerge,
  };
}

export type CatalogRowModalsState = ReturnType<typeof useCatalogRowModals>;
